/**
 * Runtime-conditional loader for libsodium-wrappers-sumo.
 *
 * Why this exists: the SUMO package's ESM entry has a broken relative import
 * (`./libsodium-sumo.mjs`) that the package doesn't actually ship. Both Bun
 * and Vite would normally follow `exports.import` and fail. We work around it
 * differently per runtime:
 *
 *  - **Server / Bun / tests:** load the CJS bundle through `createRequire`,
 *    which uses Node's classic `main`-field resolution and bypasses
 *    `exports.import`.
 *  - **Browser / Vite:** `vite.config.ts` has a `resolve.alias` that points
 *    `libsodium-wrappers-sumo` at the CJS bundle. Vite's CJS interop loads it.
 *
 * The runtime branch is selected by a `typeof process` guard, so the
 * `node:module` import is dead code in the browser. We hide it from Vite's
 * static analysis with `/* @vite-ignore *\/` so the browser build doesn't try
 * to resolve `node:module` and fail.
 */
import type SodiumLib from 'libsodium-wrappers-sumo';

declare const process: { versions?: { bun?: string; node?: string } } | undefined;

let sodium: typeof SodiumLib;

if (typeof process !== 'undefined' && (process.versions?.bun || process.versions?.node)) {
  // The frontend tsconfig has no @types/node, so it can't resolve `node:module`.
  // This branch never executes in the browser anyway (guarded by `typeof process`).
  // @ts-ignore - node:module exists in the server tsconfig (via @types/bun)
  //              but not the frontend tsconfig; @ts-ignore tolerates either.
  const { createRequire } = await import(/* @vite-ignore */ 'node:module');
  sodium = createRequire(import.meta.url)('libsodium-wrappers-sumo');
} else {
  sodium = (await import('libsodium-wrappers-sumo')).default;
}

export default sodium;
